import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { ArrowRight, CheckCircle, Copy, Check, MessageSquare, Smartphone, FileText, Search, Clock, AlertCircle, UserPlus, History, Zap, Brain, Star, Share2 as ShareIcon } from 'lucide-react';
import { AuthForm } from './auth/AuthForm';
import { useUser } from '../contexts/UserContext';
import { Link } from 'react-router-dom';
interface LandingPageProps {
  onLogin?: () => void;
}
export function LandingPage({
  onLogin
}: LandingPageProps) {
  const {
    session
  } = useUser();
  const [showAuth, setShowAuth] = useState(false);
  const [copied, setCopied] = useState(false);
  const phoneNumber = '(774) 472-7423';
  const smsHref = `sms:+1${phoneNumber.replace(/\D/g, '')}?body=Hi%20ScribeTok!%20I%20want%20to%20transcribe%20a%20video.`;
  useEffect(() => {
    if (session && onLogin) {
      onLogin();
    }
  }, [session, onLogin]);
  const handleCopyNumber = () => {
    navigator.clipboard.writeText(phoneNumber);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return <div className="min-h-screen w-full bg-background px-4 py-12 md:px-16 lg:px-32 flex flex-col items-center justify-center overflow-x-hidden" data-id="element-302">
      <div className="w-full max-w-7xl mx-auto px-4 md:px-6" data-id="element-303">
        {/* Logo */}
        <div className="flex justify-center mb-10" data-id="element-304">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/6tsscMEhsZ3L4JwT2r374e/ScribeTok-logo.png" alt="ScribeTok Logo" className="h-16 w-auto" data-id="element-305" />
        </div>

        {/* Hero Section in Bento Box Layout */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-7 mb-16" data-id="element-306">
          {/* Hero Text - Spans 7 columns on larger screens */}
          <div className="md:col-span-12 flex flex-col justify-center bg-gradient-to-br from-background to-secondary/10 p-8 md:p-10 rounded-3xl border border-border/20 shadow-sm hover:shadow-md transition-all duration-300 animate-slide-down group" style={{
          animationDelay: '100ms'
        }} data-id="element-307">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-center md:text-center leading-tight tracking-tight max-w-4xl mx-auto" data-id="element-308">
              Never Miss the Point{' '}
              <span className="text-primary bg-primary/5 px-2 rounded-lg" data-id="element-309">
                of a TikTok Again
              </span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mb-8 text-center md:text-center max-w-2xl mx-auto" data-id="element-310">
              Turn any TikTok into readable text instantly. Perfect for when
              you're busy, in meetings, or just prefer reading to watching.
            </p>
            {/* Platform Tags */}
            <div className="flex flex-wrap gap-3 mb-6 justify-center" data-id="element-311">
              <span className="bg-primary/10 text-primary px-4 py-1.5 rounded-full text-sm font-medium transition-transform hover:scale-105" data-id="element-312">
                TikTok
              </span>
              <span className="bg-primary/10 text-primary px-4 py-1.5 rounded-full text-sm font-medium transition-transform hover:scale-105" data-id="element-313">
                YouTube
              </span>
              <span className="bg-secondary/70 text-secondary-foreground px-4 py-1.5 rounded-full text-sm transition-transform hover:scale-105" data-id="element-314">
                SMS Powered
              </span>
              <span className="bg-secondary/70 text-secondary-foreground px-4 py-1.5 rounded-full text-sm transition-transform hover:scale-105" data-id="element-315">
                Instant Results
              </span>
            </div>
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mt-auto animate-fade-in justify-center max-w-2xl mx-auto w-full" style={{
            animationDelay: '300ms'
          }} data-id="element-316">
              <a href={smsHref} className="sm:min-w-[160px]" data-id="element-317">
                <Button variant="outline" size="lg" className="w-full text-base group border-primary/30 hover:border-primary/60 whitespace-nowrap px-7" data-id="element-318">
                  <MessageSquare size={18} className="mr-3 group-hover:animate-bounce text-primary flex-shrink-0" data-id="element-319" />
                  Text Now
                </Button>
              </a>
              <Button variant="tiktok" size="lg" className="w-full text-base group whitespace-nowrap sm:min-w-[200px] px-7" onClick={onLogin} data-id="element-320">
                Continue to App
                <ArrowRight size={18} className="ml-3 group-hover:translate-x-1 transition-transform flex-shrink-0" data-id="element-321" />
              </Button>
              <Link to="/features" className="sm:min-w-[200px]" data-id="element-322">
                <Button variant="secondary" size="lg" className="w-full text-base whitespace-nowrap px-7" data-id="element-323">
                  <Star size={18} className="mr-3 flex-shrink-0" data-id="element-324" />
                  Creator Features
                </Button>
              </Link>
            </div>
          </div>

          {/* Right side column - now spans full width with 2 columns */}
          <div className="md:col-span-12 grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-7" data-id="element-325">
            {/* SMS Card */}
            <Card className="overflow-hidden animate-scale border-border/20 shadow-sm hover:shadow-md transition-all duration-300 hover:border-border/40" style={{
            animationDelay: '200ms'
          }} data-id="element-326">
              <CardContent className="p-6 md:p-8 flex flex-col items-center" data-id="element-327">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-5 transition-transform hover:scale-110 duration-300" data-id="element-328">
                  <Smartphone size={28} className="text-primary" data-id="element-329" />
                </div>
                <h2 className="text-2xl font-medium mb-4" data-id="element-330">Text Us</h2>
                <div className="flex items-center gap-2 px-5 py-3 bg-secondary/70 rounded-full cursor-pointer mb-5 w-full justify-center hover:bg-secondary/90 transition-colors" onClick={handleCopyNumber} data-id="element-331">
                  <span className="font-medium" data-id="element-332">{phoneNumber}</span>
                  {copied ? <Check size={18} className="text-green-500" data-id="element-333" /> : <Copy size={18} data-id="element-334" />}
                </div>
                <img src="https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=sms:+17744727423" alt="SMS QR Code" className="mb-4 rounded-lg shadow-md transition-transform hover:scale-105 duration-300" width={150} height={150} data-id="element-335" />
                <p className="text-sm text-muted-foreground text-center" data-id="element-336">
                  Scan to open SMS on your phone
                </p>
              </CardContent>
            </Card>

            {/* App Preview/Conversation */}
            <div className="bg-secondary/20 rounded-3xl p-6 animate-fade-in overflow-hidden flex flex-col border border-border/20 shadow-sm hover:shadow-md transition-all duration-300 hover:border-border/40" style={{
            animationDelay: '300ms'
          }} data-id="element-337">
              <h3 className="text-lg font-medium mb-4" data-id="element-338">See It In Action</h3>
              <div className="space-y-3 overflow-y-auto flex-1 pr-1" data-id="element-339">
                <div className="flex justify-end" data-id="element-340">
                  <div className="tiktok-gradient text-white px-4 py-3 rounded-2xl max-w-[230px] text-sm shadow-sm break-words" data-id="element-341">
                    https://tiktok.com/@user/video/123
                  </div>
                </div>
                <div className="flex justify-start" data-id="element-342">
                  <div className="bg-gray-800 text-white px-4 py-3 rounded-2xl max-w-[230px] text-sm shadow-sm break-words" data-id="element-343">
                    <p className="flex items-start gap-2" data-id="element-344">
                      <span className="text-lg flex-shrink-0" data-id="element-345">✅</span>
                      <span data-id="element-346">Transcript ready!</span>
                    </p>
                    <p className="mt-2 ml-6 text-sm" data-id="element-347">
                      "Today I'm showing three productivity hacks..."
                    </p>
                  </div>
                </div>
                <div className="flex justify-end" data-id="element-348">
                  <div className="tiktok-gradient text-white px-4 py-3 rounded-2xl max-w-[230px] text-sm shadow-sm break-words" data-id="element-349">
                    /summary
                  </div>
                </div>
                <div className="flex justify-start" data-id="element-350">
                  <div className="bg-gray-800 text-white px-4 py-3 rounded-2xl max-w-[230px] text-sm shadow-sm break-words" data-id="element-351">
                    <p className="flex items-start gap-2" data-id="element-352">
                      <span className="text-lg flex-shrink-0" data-id="element-353">📝</span>
                      <span data-id="element-354">Time blocking, two-minute rule, digital detox</span>
                    </p>
                  </div>
                </div>
              </div>
              <Button variant="tiktok" size="lg" className="mt-4 w-full group" onClick={onLogin} data-id="element-355">
                Continue to Web App
                <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" data-id="element-356" />
              </Button>
            </div>
          </div>
        </div>

        {/* Features Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-7 mb-16" data-id="element-357">
          {/* How It Works - Spans 8 columns on larger screens */}
          <div className="md:col-span-8 bg-secondary/10 rounded-3xl p-8 animate-fade-in border border-border/20 shadow-sm hover:shadow-md transition-all duration-300 hover:border-border/40 group" style={{
          animationDelay: '400ms'
        }} data-id="element-358">
            <h3 className="text-2xl font-medium mb-6 group-hover:text-primary transition-colors" data-id="element-359">
              How It Works
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6" data-id="element-360">
              <div className="flex flex-col items-center text-center p-4 bg-background/50 rounded-2xl hover:bg-background/80 transition-colors" data-id="element-361">
                <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mb-4 transition-transform hover:scale-110 duration-300" data-id="element-362">
                  <MessageSquare size={24} className="text-primary" data-id="element-363" />
                </div>
                <h3 className="text-lg font-medium mb-2" data-id="element-364">
                  Step 1: Text a video link
                </h3>
                <p className="text-sm text-muted-foreground" data-id="element-365">
                  Send any TikTok URL to {phoneNumber}
                </p>
              </div>
              <div className="flex flex-col items-center text-center p-4 bg-background/50 rounded-2xl hover:bg-background/80 transition-colors" data-id="element-366">
                <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mb-4 transition-transform hover:scale-110 duration-300" data-id="element-367">
                  <FileText size={24} className="text-primary" data-id="element-368" />
                </div>
                <h3 className="text-lg font-medium mb-2" data-id="element-369">
                  Step 2: Wait a moment
                </h3>
                <p className="text-sm text-muted-foreground" data-id="element-370">
                  Our AI extracts the text quickly
                </p>
              </div>
              <div className="flex flex-col items-center text-center p-4 bg-background/50 rounded-2xl hover:bg-background/80 transition-colors" data-id="element-371">
                <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mb-4 transition-transform hover:scale-110 duration-300" data-id="element-372">
                  <ShareIcon size={24} className="text-primary" data-id="element-373" />
                </div>
                <h3 className="text-lg font-medium mb-2" data-id="element-374">
                  Step 3: Get your transcript
                </h3>
                <p className="text-sm text-muted-foreground" data-id="element-375">
                  Receive text with a shareable link
                </p>
              </div>
            </div>
          </div>

          {/* Key Benefits - Takes 4 columns */}
          <div className="md:col-span-4 bg-primary/5 rounded-3xl p-8 animate-fade-in border border-primary/10 shadow-sm hover:shadow-md transition-all duration-300 hover:border-primary/20 group" style={{
          animationDelay: '500ms'
        }} data-id="element-376">
            <h3 className="text-2xl font-medium mb-6 flex items-center group-hover:text-primary transition-colors" data-id="element-377">
              <Zap size={20} className="text-primary mr-3" data-id="element-378" />
              Key Benefits
            </h3>
            <div className="space-y-5" data-id="element-379">
              <div className="flex items-start gap-4 p-3 bg-background/50 rounded-2xl hover:bg-background/80 transition-colors" data-id="element-380">
                <History size={20} className="text-primary mt-1 flex-shrink-0" data-id="element-381" />
                <div data-id="element-382">
                  <p className="font-medium text-base" data-id="element-383">No Account Needed</p>
                  <p className="text-sm text-muted-foreground" data-id="element-384">
                    Transcripts saved to your phone number
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-3 bg-background/50 rounded-2xl hover:bg-background/80 transition-colors" data-id="element-385">
                <UserPlus size={20} className="text-primary mt-1 flex-shrink-0" data-id="element-386" />
                <div data-id="element-387">
                  <p className="font-medium text-base" data-id="element-388">Create Account Later</p>
                  <p className="text-sm text-muted-foreground" data-id="element-389">
                    Text <span className="font-medium" data-id="element-390">/register</span> anytime
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-3 bg-background/50 rounded-2xl hover:bg-background/80 transition-colors" data-id="element-391">
                <Brain size={20} className="text-primary mt-1 flex-shrink-0" data-id="element-392" />
                <div data-id="element-393">
                  <p className="font-medium text-base" data-id="element-394">Powerful Commands</p>
                  <p className="text-sm text-muted-foreground" data-id="element-395">
                    Use <span className="font-medium" data-id="element-396">/summary</span> for
                    insights
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Trust and Commands Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-7 mb-16" data-id="element-397">
          {/* Trust Indicators - Takes 4 columns */}
          <div className="md:col-span-4 flex flex-col justify-center bg-secondary/10 rounded-3xl p-8 animate-fade-in border border-border/20 shadow-sm hover:shadow-md transition-all duration-300 hover:border-border/40" style={{
          animationDelay: '600ms'
        }} data-id="element-398">
            <h3 className="text-2xl font-medium mb-6" data-id="element-399">Why Trust Us</h3>
            <div className="space-y-4" data-id="element-400">
              <div className="flex items-center gap-3 p-3 bg-background/50 rounded-2xl hover:bg-background/80 transition-colors" data-id="element-401">
                <CheckCircle size={22} className="text-success" data-id="element-402" />
                <span className="text-base font-medium" data-id="element-403">Fast & Accurate</span>
              </div>
              <div className="flex items-center gap-3 p-3 bg-background/50 rounded-2xl hover:bg-background/80 transition-colors" data-id="element-404">
                <CheckCircle size={22} className="text-success" data-id="element-405" />
                <span className="text-base font-medium" data-id="element-406">
                  Multiple Languages
                </span>
              </div>
              <div className="flex items-center gap-3 p-3 bg-background/50 rounded-2xl hover:bg-background/80 transition-colors" data-id="element-407">
                <CheckCircle size={22} className="text-success" data-id="element-408" />
                <span className="text-base font-medium" data-id="element-409">
                  10,000+ Videos Daily
                </span>
              </div>
            </div>
          </div>

          {/* SMS Commands - Takes 8 columns */}
          <div className="md:col-span-8 rounded-3xl overflow-hidden animate-fade-in" style={{
          animationDelay: '700ms'
        }} data-id="element-410">
            <div className="bg-secondary/10 p-8 rounded-3xl border border-border/20 shadow-sm hover:shadow-md transition-all duration-300 hover:border-border/40" data-id="element-411">
              <h3 className="text-2xl font-medium mb-6" data-id="element-412">SMS Commands</h3>
              <div className="grid grid-cols-2 gap-4" data-id="element-413">
                <div className="bg-secondary/30 p-4 rounded-xl hover:bg-secondary/50 transition-colors hover:scale-105 transform duration-300 border border-border/20" data-id="element-414">
                  <p className="font-medium text-base" data-id="element-415">/help</p>
                  <p className="text-sm text-muted-foreground" data-id="element-416">
                    List all commands
                  </p>
                </div>
                <div className="bg-secondary/30 p-4 rounded-xl hover:bg-secondary/50 transition-colors hover:scale-105 transform duration-300 border border-border/20" data-id="element-417">
                  <p className="font-medium text-base" data-id="element-418">/register</p>
                  <p className="text-sm text-muted-foreground" data-id="element-419">
                    Create account
                  </p>
                </div>
                <div className="bg-secondary/30 p-4 rounded-xl hover:bg-secondary/50 transition-colors hover:scale-105 transform duration-300 border border-border/20" data-id="element-420">
                  <p className="font-medium text-base" data-id="element-421">/summary</p>
                  <p className="text-sm text-muted-foreground" data-id="element-422">
                    Get quick insights
                  </p>
                </div>
                <div className="bg-secondary/30 p-4 rounded-xl hover:bg-secondary/50 transition-colors hover:scale-105 transform duration-300 border border-border/20" data-id="element-423">
                  <p className="font-medium text-base" data-id="element-424">/vault</p>
                  <p className="text-sm text-muted-foreground" data-id="element-425">
                    View recent transcripts
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Full Conversation Example - Now in a more contained format */}

        {/* Final CTA */}
        <div className="mb-16 animate-fade-in" style={{
        animationDelay: '800ms'
      }} data-id="element-426">
          <div className="bg-secondary/10 rounded-3xl p-6 md:p-8 border border-border/20 shadow-sm hover:shadow-md transition-all duration-300 hover:border-border/40" data-id="element-427">
            <div className="flex items-center justify-between mb-5" data-id="element-428">
              <h3 className="text-2xl font-medium" data-id="element-429">
                Complete Conversation Flow
              </h3>
              <div className="bg-primary/10 text-primary px-3 py-1 rounded-full text-xs font-medium" data-id="element-430">
                Real-time Experience
              </div>
            </div>
            <div className="max-w-2xl mx-auto bg-background/50 p-5 rounded-2xl" data-id="element-431">
              <div className="space-y-4" data-id="element-432">
                {/* First message */}
                <div className="flex justify-end" data-id="element-433">
                  <div className="tiktok-gradient text-white px-4 py-3 rounded-2xl max-w-[280px] shadow-sm break-words hover:scale-105 transition-transform" data-id="element-434">
                    https://www.tiktok.com/@username/video/1234567890
                  </div>
                </div>
                {/* Response messages */}
                <div className="flex justify-start" data-id="element-435">
                  <div className="bg-gray-800 text-white px-4 py-3 rounded-2xl max-w-[280px] shadow-sm break-words hover:scale-105 transition-transform" data-id="element-436">
                    <p className="flex items-start gap-2" data-id="element-437">
                      <span className="text-xl flex-shrink-0" data-id="element-438">🎬</span>
                      <span data-id="element-439">
                        Thanks for using ScribeTok! You'll receive a transcript
                        shortly.
                      </span>
                    </p>
                  </div>
                </div>
                <div className="flex justify-start" data-id="element-440">
                  <div className="bg-gray-800 text-white px-4 py-3 rounded-2xl max-w-[280px] shadow-sm break-words hover:scale-105 transition-transform" data-id="element-441">
                    <p className="flex items-start gap-2" data-id="element-442">
                      <span className="text-xl flex-shrink-0" data-id="element-443">⏳</span>
                      <span data-id="element-444">Got your link! Transcribing now...</span>
                    </p>
                  </div>
                </div>
                {/* Transcript result */}
                <div className="flex justify-start" data-id="element-445">
                  <div className="bg-gray-800 text-white px-4 py-3 rounded-2xl max-w-[350px] shadow-sm break-words hover:scale-105 transition-transform" data-id="element-446">
                    <p className="flex items-start gap-2" data-id="element-447">
                      <span className="text-xl flex-shrink-0" data-id="element-448">✅</span>
                      <span data-id="element-449">Transcript ready!</span>
                    </p>
                    <p className="mt-3 ml-7" data-id="element-450">
                      "Hey everyone, today I'm going to show you three
                      productivity hacks that changed my life..."
                    </p>
                    <p className="text-xs mt-3 text-primary font-medium ml-7" data-id="element-451">
                      share.scribetok.com/v/b70c860b-d7dc
                    </p>
                  </div>
                </div>
                {/* Summary request */}
                <div className="flex justify-end" data-id="element-452">
                  <div className="tiktok-gradient text-white px-4 py-3 rounded-2xl max-w-[280px] shadow-sm break-words hover:scale-105 transition-transform" data-id="element-453">
                    /summary
                  </div>
                </div>
                {/* Summary response */}
                <div className="flex justify-start" data-id="element-454">
                  <div className="bg-gray-800 text-white px-4 py-3 rounded-2xl max-w-[350px] shadow-sm break-words hover:scale-105 transition-transform" data-id="element-455">
                    <p className="flex items-start gap-2" data-id="element-456">
                      <span className="text-xl flex-shrink-0" data-id="element-457">📝</span>
                      <span className="font-medium" data-id="element-458">Summary:</span>
                    </p>
                    <p className="mt-3 ml-7" data-id="element-459">
                      Three productivity hacks: time blocking, two-minute rule
                      for small tasks, and digital detox every evening.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-center mt-6" data-id="element-460">
              <div className="flex items-center gap-3 text-sm text-muted-foreground bg-secondary/30 px-4 py-2 rounded-full" data-id="element-461">
                <span className="text-primary" data-id="element-462">🔄</span>
                <span data-id="element-463">Experience this yourself in seconds</span>
              </div>
            </div>
          </div>
        </div>

        {/* Compliance Footer */}
        <div className="mt-8 border-t border-border/30 pt-6 text-sm text-muted-foreground" data-id="element-464">
          <p data-id="element-465">
            ScribeTok is a user-initiated SMS service. We do not send
            promotional messages. You can opt out at any time by replying STOP.
            Msg & data rates may apply.
          </p>
          <p className="mt-3" data-id="element-466">
            <Link to="/text/compliance" className="text-primary hover:underline" data-id="element-467">
              View full terms
            </Link>
          </p>
        </div>
      </div>
    </div>;
}